import { TOTP_CODE_LENGTH, TOTP_CODE_PATTERN } from '@gozoki/api-types/src/validation/adminMfa'
import { z } from 'zod'

/*
 * Fin d'une preuve d'identité à la connexion du back-office (mot de passe, code de
 * réinitialisation) : le jeton de session, ou, pour un admin enrôlé au double facteur (S1.4a),
 * le défi que l'étape « code » lève par `POST /auth/login-admin/mfa` (S1.4b).
 */

/** Message d'un échec sans réponse exploitable (réseau, erreur serveur). */
export const UNKNOWN_ERROR_MESSAGE = 'Une erreur est survenue, réessayez plus tard'

export type SessionStart = { token: string } | { challengeToken: string }

/** Défi du double facteur, rendu à la place du jeton pour un admin enrôlé. */
export interface MfaChallengeResponse {
  mfaRequired: true
  challengeToken: string
}

/** Jeton sérialisé du serveur (`{ type: 'bearer', token, … }`). */
export interface SessionTokenResponse {
  token?: string | null
}

/**
 * Jeton ou défi rendu par le serveur. Tuyau lève sur toute réponse non 2xx : une réponse sans l'un
 * ni l'autre ne vient que du typage (branche du limiteur, qui répond sans corps typé).
 */
export const sessionStartOf = (
  response: MfaChallengeResponse | SessionTokenResponse | null | undefined,
): SessionStart => {
  if (response && 'mfaRequired' in response) return { challengeToken: response.challengeToken }
  const token = response?.token
  if (!token) throw new Error(UNKNOWN_ERROR_MESSAGE)
  return { token }
}

/** Code de secours `XXXX-XXXX-XXXX-XXXX` : seize caractères, tirets en plus. */
const RECOVERY_CODE_CHARACTERS = 16
const RECOVERY_CODE_MAX_LENGTH = RECOVERY_CODE_CHARACTERS + 3

/** Ne garde que les chiffres du code collé ou saisi (`123 456`), six au plus. */
export const normalizeTotpCode = (value: string) =>
  value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH)

/** Majuscules, lettres, chiffres et tirets seulement : la forme imprimée d'un code de secours. */
export const normalizeRecoveryCode = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '')
    .slice(0, RECOVERY_CODE_MAX_LENGTH)

export const zTotpCode = z.object({
  code: z
    .string()
    .regex(TOTP_CODE_PATTERN, `Le code de l'application contient ${TOTP_CODE_LENGTH} chiffres`),
})

export const zRecoveryCode = z.object({
  code: z
    .string()
    .refine(
      (code) => code.replace(/-/g, '').length === RECOVERY_CODE_CHARACTERS,
      'Un code de secours a la forme XXXX-XXXX-XXXX-XXXX',
    ),
})

export type CodeKind = 'totp' | 'recovery'

/** Champ du code, selon sa sorte : étape « code » de la connexion et confirmation de l'enrôlement. */
export const CODE_FIELD = {
  totp: {
    label: "Code de l'application",
    placeholder: '123456',
    inputMode: 'numeric',
    transform: normalizeTotpCode,
    schema: zTotpCode,
    description: `Saisissez le code à ${TOTP_CODE_LENGTH} chiffres affiché par votre application d'authentification.`,
  },
  recovery: {
    label: 'Code de secours',
    placeholder: 'XXXX-XXXX-XXXX-XXXX',
    inputMode: 'text',
    transform: normalizeRecoveryCode,
    schema: zRecoveryCode,
    description: "Saisissez l'un de vos codes de secours. Chaque code ne sert qu'une fois.",
  },
} as const
